import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Tabs } from '@haltongroup/halton-ui-library';
import { SavedOffer, SavedWorkOrder, MaintenanceReport, SavedAnsulCommissioning } from '../types';
import {
  deleteOffer,
  deleteWorkOrder,
  deleteMaintenanceReport,
  deleteAnsulCommissioning,
  exportQuoteStoreOffers,
  formatCurrency,
  formatDate,
  exportDocuments,
  importDocuments,
  ImportResult
} from '../utils';
import { exportToExcel } from '../utils/exportToExcel';

interface ArchiveProps {
  savedOffers: SavedOffer[];
  savedWorkOrders: SavedWorkOrder[];
  maintenanceReports: MaintenanceReport[];
  ansulCommissionings: SavedAnsulCommissioning[];
  onEditOffer: (offer: SavedOffer) => void;
  onPrintOffer: (offer: SavedOffer) => void;
  onPrintWorkOrder: (workOrder: SavedWorkOrder) => void;
  onEditWorkOrder: (workOrder: SavedWorkOrder) => void;
  onCopyOffer: (offer: SavedOffer) => void;
  onCopyWorkOrder: (workOrder: SavedWorkOrder) => void;
  onPrintMaintenanceReport: (report: MaintenanceReport) => void;
  onEditMaintenanceReport: (report: MaintenanceReport) => void;
  onEditAnsulCommissioning: (report: SavedAnsulCommissioning) => void;
  onPrintAnsulCommissioning: (report: SavedAnsulCommissioning) => void;
  onCreateWorkOrderFromOffer: (offer: SavedOffer) => void;
  onRefresh: () => void;
}

type TabType = 'offers' | 'workorders' | 'serviceReports';

// Documents saved before the placeholder normalization may still carry
// master enrichment placeholders in their address text; hide them in lists.
const cleanDisplayAddress = (address: string | undefined): string =>
  (address || '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part && !/pending enrichment/i.test(part))
    .join(', ');

const Archive = ({
  savedOffers,
  savedWorkOrders,
  maintenanceReports,
  ansulCommissionings,
  onEditOffer,
  onPrintOffer,
  onPrintWorkOrder,
  onEditWorkOrder,
  onCopyOffer,
  onCopyWorkOrder,
  onPrintMaintenanceReport,
  onEditMaintenanceReport,
  onEditAnsulCommissioning,
  onPrintAnsulCommissioning,
  onCreateWorkOrderFromOffer,
  onRefresh
}: ArchiveProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('offers');
  const [searchTerm, setSearchTerm] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Selection state
  const [selectedOfferIds, setSelectedOfferIds] = useState<Set<string>>(new Set());
  const [selectedWorkOrderIds, setSelectedWorkOrderIds] = useState<Set<string>>(new Set());
  const [selectedMaintenanceReportIds, setSelectedMaintenanceReportIds] = useState<Set<string>>(new Set());
  const [selectedAnsulCommissioningIds, setSelectedAnsulCommissioningIds] = useState<Set<string>>(new Set());
  const [showMaintenanceFilter, setShowMaintenanceFilter] = useState(true);
  const [showCommissioningFilter, setShowCommissioningFilter] = useState(true);

  const handleDeleteOffer = (id: string) => {
    if (window.confirm(t('archive.confirmDeleteQuote'))) {
      deleteOffer(id);
      setSelectedOfferIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      onRefresh();
    }
  };

  const handleDeleteWorkOrder = (id: string) => {
    if (window.confirm(t('archive.confirmDeleteWorkOrder'))) {
      deleteWorkOrder(id);
      setSelectedWorkOrderIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      onRefresh();
    }
  };

  const handleDeleteMaintenanceReport = (id: string) => {
    if (window.confirm(t('archive.confirmDeleteMaintenanceReport'))) {
      deleteMaintenanceReport(id);
      setSelectedMaintenanceReportIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      onRefresh();
    }
  };

  const handleDeleteAnsulCommissioning = (id: string) => {
    if (window.confirm(t('archive.confirmDeleteAnsulCommissioning'))) {
      deleteAnsulCommissioning(id);
      setSelectedAnsulCommissioningIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      onRefresh();
    }
  };

  // Selection handlers
  const toggleOfferSelection = (id: string) => {
    setSelectedOfferIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleWorkOrderSelection = (id: string) => {
    setSelectedWorkOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleMaintenanceReportSelection = (id: string) => {
    setSelectedMaintenanceReportIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAnsulCommissioningSelection = (id: string) => {
    setSelectedAnsulCommissioningIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllOffers = () => {
    setSelectedOfferIds(new Set(filteredOffers.map(o => o.id)));
  };

  const deselectAllOffers = () => {
    setSelectedOfferIds(new Set());
  };

  const selectAllWorkOrders = () => {
    setSelectedWorkOrderIds(new Set(filteredWorkOrders.map(wo => wo.id)));
  };

  const deselectAllWorkOrders = () => {
    setSelectedWorkOrderIds(new Set());
  };

  const selectAllMaintenanceReports = () => {
    setSelectedMaintenanceReportIds(new Set(filteredMaintenanceReports.map(mr => mr.id)));
  };

  const deselectAllMaintenanceReports = () => {
    setSelectedMaintenanceReportIds(new Set());
  };

  const selectAllAnsulCommissionings = () => {
    setSelectedAnsulCommissioningIds(new Set(filteredAnsulCommissionings.map(ac => ac.id)));
  };

  const deselectAllAnsulCommissionings = () => {
    setSelectedAnsulCommissioningIds(new Set());
  };

  const selectAllServiceReports = () => {
    selectAllMaintenanceReports();
    selectAllAnsulCommissionings();
  };

  const deselectAllServiceReports = () => {
    deselectAllMaintenanceReports();
    deselectAllAnsulCommissionings();
  };

  const clearAllSelections = () => {
    setSelectedOfferIds(new Set());
    setSelectedWorkOrderIds(new Set());
    setSelectedMaintenanceReportIds(new Set());
    setSelectedAnsulCommissioningIds(new Set());
  };

  // Bulk delete handler
  const handleDeleteSelected = () => {
    const totalToDelete = selectedOfferIds.size + selectedWorkOrderIds.size + selectedMaintenanceReportIds.size + selectedAnsulCommissioningIds.size;
    if (totalToDelete === 0) return;

    const parts: string[] = [];
    if (selectedOfferIds.size > 0) parts.push(`${selectedOfferIds.size} ${t('archive.tabs.quotes').toLowerCase()}`);
    if (selectedWorkOrderIds.size > 0) parts.push(`${selectedWorkOrderIds.size} ${t('archive.tabs.workOrders').toLowerCase()}`);
    if (selectedMaintenanceReportIds.size > 0) parts.push(`${selectedMaintenanceReportIds.size} ${t('archive.filter.maintenance').toLowerCase()}`);
    if (selectedAnsulCommissioningIds.size > 0) parts.push(`${selectedAnsulCommissioningIds.size} ${t('archive.filter.commissioning').toLowerCase()}`);

    if (window.confirm(t('archive.confirmDeleteSelected', { items: parts.join(', ') }))) {
      selectedOfferIds.forEach(id => deleteOffer(id));
      selectedWorkOrderIds.forEach(id => deleteWorkOrder(id));
      selectedMaintenanceReportIds.forEach(id => deleteMaintenanceReport(id));
      selectedAnsulCommissioningIds.forEach(id => deleteAnsulCommissioning(id));
      clearAllSelections();
      onRefresh();
    }
  };

  // Export handler (Excel)
  const handleExport = () => {
    const selectedOffers = savedOffers.filter(o => selectedOfferIds.has(o.id));
    const selectedWorkOrders = savedWorkOrders.filter(wo => selectedWorkOrderIds.has(wo.id));
    const selectedMaintenanceReports = maintenanceReports.filter(mr => selectedMaintenanceReportIds.has(mr.id));
    // @ts-ignore - exportToExcel function signature might need updating to accept 3 args
    exportToExcel(selectedOffers, selectedWorkOrders, selectedMaintenanceReports);
  };

  // Export handler (JSON - for sharing between users)
  const handleExportJson = () => {
    const selectedOffers = savedOffers.filter(o => selectedOfferIds.has(o.id));
    const selectedWorkOrders = savedWorkOrders.filter(wo => selectedWorkOrderIds.has(wo.id));
    exportDocuments(selectedWorkOrders, selectedOffers);
  };

  const handleExportQuoteStoreJson = () => {
    const selectedOffers = savedOffers.filter(o => selectedOfferIds.has(o.id));
    if (selectedOffers.length === 0) {
      window.alert(t('archive.quoteStore.noQuotesSelected'));
      return;
    }

    try {
      exportQuoteStoreOffers(selectedOffers);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'QuoteStore export failed');
    }
  };

  // Import handler (JSON - for importing shared documents)
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const result = await importDocuments(file);
    setImportResult(result);

    if (result.success) {
      onRefresh();
      // Clear import result message after 5 seconds
      setTimeout(() => setImportResult(null), 5000);
    }

    // Reset file input so the same file can be imported again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Filter offers
  const filteredOffers = savedOffers.filter(offer =>
    offer.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    offer.documentNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter work orders
  const filteredWorkOrders = savedWorkOrders.filter(wo =>
    wo.formData.yritys.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wo.formData.kohde.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wo.documentNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (wo.masterWorkOrderNumber || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter maintenance reports
  const filteredMaintenanceReports = maintenanceReports.filter(mr =>
    mr.formData.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mr.formData.siteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mr.documentNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter ansul commissionings
  const filteredAnsulCommissionings = ansulCommissionings.filter(ac => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (ac.formData?.restaurantName?.toLowerCase().includes(searchLower)) ||
      (ac.formData?.restaurantAddress?.toLowerCase().includes(searchLower)) ||
      (ac.documentNumber?.toLowerCase().includes(searchLower)) ||
      (ac.formData?.contactPerson?.toLowerCase().includes(searchLower))
    );
  });

  const totalSelected = selectedOfferIds.size + selectedWorkOrderIds.size + selectedMaintenanceReportIds.size + selectedAnsulCommissioningIds.size;

  return (
    <div className="archive-page">
      <div className="page-header">
        <h1>{t('archive.title')}</h1>
        <p className="page-subtitle">{t('archive.subtitle')}</p>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as TabType)}
        items={[
          { key: 'offers', title: `${t('archive.tabs.quotes')} (${savedOffers.length})`, content: null },
          { key: 'workorders', title: `${t('archive.tabs.workOrders')} (${savedWorkOrders.length})`, content: null },
          { key: 'serviceReports', title: `${t('archive.tabs.serviceReports')} (${maintenanceReports.length + ansulCommissionings.length})`, content: null },
        ]}
      />

      {/* Search and Selection Actions */}
      <div className="archive-toolbar">
        <div className="archive-search">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('archive.searchPlaceholder')}
            className="form-input search-input"
          />
        </div>
        <div className="archive-select-actions">
          {/* Import Button */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".json"
            style={{ display: 'none' }}
          />
          <Button theme="tertiary" size="small" label={t('archive.import')} onClick={handleImportClick} />

          {activeTab === 'offers' && (
            <>
              <Button theme="tertiary" size="small" label={t('archive.selectAll')} onClick={selectAllOffers} />
              <Button theme="tertiary" size="small" label={t('archive.deselectAll')} onClick={deselectAllOffers} />
              <Button theme="secondary" size="small" label={t('archive.quoteStore.export')} onClick={handleExportQuoteStoreJson} />
            </>
          )}
          {activeTab === 'workorders' && (
            <>
              <Button theme="tertiary" size="small" label={t('archive.selectAll')} onClick={selectAllWorkOrders} />
              <Button theme="tertiary" size="small" label={t('archive.deselectAll')} onClick={deselectAllWorkOrders} />
            </>
          )}
          {activeTab === 'serviceReports' && (
            <>
              <Button theme="tertiary" size="small" label={t('archive.selectAll')} onClick={selectAllServiceReports} />
              <Button theme="tertiary" size="small" label={t('archive.deselectAll')} onClick={deselectAllServiceReports} />
            </>
          )}
        </div>
      </div>

      {/* Import Result Message */}
      {importResult && (
        <div className={`import-result ${importResult.success ? 'success' : 'error'}`}>
          {importResult.success ? (
            <span>
              ✅ {t('archive.importSuccess', {
                workOrders: importResult.workOrdersImported,
                offers: importResult.offersImported,
                ansulCommissionings: importResult.ansulCommissioningsImported
              })}
            </span>
          ) : (
            <span>❌ {importResult.errors.join(', ')}</span>
          )}
          <button className="btn-close" onClick={() => setImportResult(null)}>×</button>
        </div>
      )}

      {/* Offers Tab */}
      {activeTab === 'offers' && (
        <div className="archive-content">
          {filteredOffers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h3>{t('archive.emptyQuotes')}</h3>
              <p>{t('archive.emptyQuotesHint')}</p>
            </div>
          ) : (
            <div className="archive-list">
              {filteredOffers.map(offer => (
                <div key={offer.id} className={`archive-card ${selectedOfferIds.has(offer.id) ? 'selected' : ''}`}>
                  <div className="archive-card-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedOfferIds.has(offer.id)}
                      onChange={() => toggleOfferSelection(offer.id)}
                      className="archive-checkbox"
                    />
                  </div>
                  <div className="archive-card-content">
                    <div className="archive-card-header">
                      <div className="archive-card-title">
                        <span className="document-number">{offer.documentNumber}</span>
                        {offer.jobNumber && <span className="job-number">{offer.jobNumber}</span>}
                        <span className="customer-name">{offer.customerName || t('archive.noCustomer')}</span>
                      </div>
                      <div className="archive-card-meta">
                        <span className="archive-date">{formatDate(offer.createdAt)}</span>
                      </div>
                    </div>

                    <div className="archive-card-body">
                      <div className="archive-card-info">
                        <span>{cleanDisplayAddress(offer.customerAddress) || t('archive.noAddress')}</span>
                      </div>
                      <div className="archive-card-total">
                        <span className="total-label">{t('archive.total')}:</span>
                        <span className="total-value">{formatCurrency(offer.totals.total, offer.settings.companyCountry)}</span>
                      </div>
                    </div>

                    <div className="archive-card-actions">
                      <Button
                        theme="success"
                        size="small"
                        label={t('archive.actions.createWorkOrder')}
                        onClick={() => onCreateWorkOrderFromOffer(offer)}
                      />
                      <Button
                        theme="tertiary"
                        size="small"
                        label={t('archive.actions.copy')}
                        onClick={() => onCopyOffer(offer)}
                      />
                      <Button
                        theme="secondary"
                        size="small"
                        label={t('archive.actions.edit')}
                        onClick={() => onEditOffer(offer)}
                      />
                      <Button
                        theme="primary"
                        size="small"
                        label={t('archive.actions.print')}
                        onClick={() => onPrintOffer(offer)}
                      />
                      <Button
                        theme="danger"
                        size="small"
                        label={t('archive.actions.delete')}
                        onClick={() => handleDeleteOffer(offer.id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Work Orders Tab */}
      {activeTab === 'workorders' && (
        <div className="archive-content">
          {filteredWorkOrders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>{t('archive.emptyWorkOrders')}</h3>
              <p>{t('archive.emptyWorkOrdersHint')}</p>
            </div>
          ) : (
            <div className="archive-list">
              {filteredWorkOrders.map(wo => (
                <div key={wo.id} className={`archive-card ${selectedWorkOrderIds.has(wo.id) ? 'selected' : ''}`}>
                  <div className="archive-card-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedWorkOrderIds.has(wo.id)}
                      onChange={() => toggleWorkOrderSelection(wo.id)}
                      className="archive-checkbox"
                    />
                  </div>
                  <div className="archive-card-content">
                    <div className="archive-card-header">
                      <div className="archive-card-title">
                        <span className="document-number">{wo.documentNumber}</span>
                        {wo.masterWorkOrderNumber && (
                          <span data-testid="master-wo-chip">
                            <Chip label={wo.masterWorkOrderNumber} theme="success" border />
                          </span>
                        )}
                        {wo.formData.jobNumber && <span className="job-number">{wo.formData.jobNumber}</span>}
                        <span className="customer-name">{wo.formData.yritys || wo.formData.yhteyshenkilo || t('archive.noCustomer')}</span>
                      </div>
                      <div className="archive-card-meta">
                        {wo.formData.lineOfBusiness && (
                          <Chip label={wo.formData.lineOfBusiness.toUpperCase()} theme="info" border />
                        )}
                        <span className="archive-date">{formatDate(wo.createdAt)}</span>
                      </div>
                    </div>

                    <div className="archive-card-body">
                      <div className="archive-card-info">
                        <span><strong>{t('workOrder.order.site')}:</strong> {wo.formData.kohde || 'N/A'}</span>
                        <span><strong>{t('workOrder.order.reference')}:</strong> {wo.formData.viite || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="archive-card-actions">
                      <Button
                        theme="tertiary"
                        size="small"
                        label={t('archive.actions.copy')}
                        onClick={() => onCopyWorkOrder(wo)}
                      />
                      <Button
                        theme="secondary"
                        size="small"
                        label={t('archive.actions.edit')}
                        onClick={() => onEditWorkOrder(wo)}
                      />
                      <Button
                        theme="primary"
                        size="small"
                        label={t('archive.actions.print')}
                        onClick={() => onPrintWorkOrder(wo)}
                      />
                      <Button
                        theme="danger"
                        size="small"
                        label={t('archive.actions.delete')}
                        onClick={() => handleDeleteWorkOrder(wo.id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Service Reports Tab */}
      {activeTab === 'serviceReports' && (
        <div className="archive-content">
          {/* Filter checkboxes */}
          <div className="archive-filters">
            <label>
              <input
                type="checkbox"
                checked={showMaintenanceFilter}
                onChange={() => setShowMaintenanceFilter(!showMaintenanceFilter)}
              />
              {t('archive.filter.maintenance')}
            </label>
            <label>
              <input
                type="checkbox"
                checked={showCommissioningFilter}
                onChange={() => setShowCommissioningFilter(!showCommissioningFilter)}
              />
              {t('archive.filter.commissioning')}
            </label>
          </div>

          {filteredMaintenanceReports.length === 0 && filteredAnsulCommissionings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔧</div>
              <h3>{t('archive.emptyMaintenanceReports')}</h3>
              <p>{t('archive.emptyMaintenanceReportsHint')}</p>
            </div>
          ) : (
            <div className="archive-list">
              {/* Maintenance Reports */}
              {showMaintenanceFilter && filteredMaintenanceReports.map(report => (
                <div key={report.id} className={`archive-card ${selectedMaintenanceReportIds.has(report.id) ? 'selected' : ''}`}>
                  <div className="archive-card-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedMaintenanceReportIds.has(report.id)}
                      onChange={() => toggleMaintenanceReportSelection(report.id)}
                      className="archive-checkbox"
                    />
                  </div>
                  <div className="archive-card-content">
                    <div className="archive-card-header">
                      <div className="archive-card-title">
                        <span className="document-number">{report.documentNumber}</span>
                        <span className="customer-name">{report.formData.customerName || t('archive.noCustomer')}</span>
                      </div>
                      <div className="archive-card-meta">
                        <span className="archive-date">{formatDate(report.createdAt)}</span>
                      </div>
                    </div>

                    <div className="archive-card-body">
                      <div className="archive-card-info">
                        <span><strong>{t('maintenance.report.type')}:</strong> {
                          // Handle both old format (type: string) and new format (types: string[])
                          Array.isArray(report.formData.types) 
                            ? report.formData.types.map(type => t(`maintenance.types.${type}`)).join(', ')
                            : t(`maintenance.types.${(report.formData as any).type || 'kcw_ww_technical'}`)
                        }</span>
                        <span><strong>{t('maintenance.report.site')}:</strong> {report.formData.siteName || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="archive-card-actions">
                      <Button
                        theme="secondary"
                        size="small"
                        label={t('archive.actions.edit')}
                        onClick={() => onEditMaintenanceReport(report)}
                      />
                      <Button
                        theme="primary"
                        size="small"
                        label={t('archive.actions.print')}
                        onClick={() => onPrintMaintenanceReport(report)}
                      />
                      <Button
                        theme="danger"
                        size="small"
                        label={t('archive.actions.delete')}
                        onClick={() => handleDeleteMaintenanceReport(report.id)}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Ansul Commissioning Reports */}
              {showCommissioningFilter && filteredAnsulCommissionings.map(report => (
                <div key={report.id} className={`archive-card ${selectedAnsulCommissioningIds.has(report.id) ? 'selected' : ''}`}>
                  <div className="archive-card-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedAnsulCommissioningIds.has(report.id)}
                      onChange={() => toggleAnsulCommissioningSelection(report.id)}
                      className="archive-checkbox"
                    />
                  </div>
                  <div className="archive-card-content">
                    <div className="archive-card-header">
                      <div className="archive-card-title">
                        <span className="document-number">{report.documentNumber}</span>
                        <span className="customer-name">{report.formData.restaurantName || 'No restaurant'}</span>
                      </div>
                      <div className="archive-card-meta">
                        <span className="archive-date">{formatDate(report.createdAt)}</span>
                      </div>
                    </div>

                    <div className="archive-card-body">
                      <div className="archive-card-info">
                        <span><strong>{t('ansulCommissioning.system.type')}:</strong> {report.formData.systemType || 'N/A'}</span>
                        <span><strong>{t('ansulCommissioning.schedule.commissioningDate')}:</strong> {report.formData.commissioningDate || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="archive-card-actions">
                      <Button
                        theme="secondary"
                        size="small"
                        label={t('archive.actions.edit')}
                        onClick={() => onEditAnsulCommissioning(report)}
                      />
                      <Button
                        theme="primary"
                        size="small"
                        label={t('archive.actions.print')}
                        onClick={() => onPrintAnsulCommissioning(report)}
                      />
                      <Button
                        theme="danger"
                        size="small"
                        label={t('archive.actions.delete')}
                        onClick={() => handleDeleteAnsulCommissioning(report.id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Export Toolbar - shows when items are selected */}
      {totalSelected > 0 && (
        <div className="export-toolbar">
          <div className="export-toolbar-info">
            <span className="export-count">
              {selectedOfferIds.size > 0 && `${selectedOfferIds.size} ${t('archive.tabs.quotes').toLowerCase()}`}
              {selectedOfferIds.size > 0 && (selectedWorkOrderIds.size > 0 || selectedMaintenanceReportIds.size > 0 || selectedAnsulCommissioningIds.size > 0) && ', '}
              {selectedWorkOrderIds.size > 0 && `${selectedWorkOrderIds.size} ${t('archive.tabs.workOrders').toLowerCase()}`}
              {selectedWorkOrderIds.size > 0 && (selectedMaintenanceReportIds.size > 0 || selectedAnsulCommissioningIds.size > 0) && ', '}
              {selectedMaintenanceReportIds.size > 0 && `${selectedMaintenanceReportIds.size} ${t('archive.filter.maintenance').toLowerCase()}`}
              {selectedMaintenanceReportIds.size > 0 && selectedAnsulCommissioningIds.size > 0 && ', '}
              {selectedAnsulCommissioningIds.size > 0 && `${selectedAnsulCommissioningIds.size} ${t('archive.filter.commissioning').toLowerCase()}`}
              {' '}{t('archive.selected')}
            </span>
          </div>
          <div className="export-toolbar-actions">
            <Button theme="tertiary" label={t('archive.clearSelection')} onClick={clearAllSelections} />
            <Button theme="danger" label={t('archive.deleteSelected')} onClick={handleDeleteSelected} />
            {/* JSON Export - for sharing work orders and quotes between users */}
            {(selectedOfferIds.size > 0 || selectedWorkOrderIds.size > 0) && (
              <Button theme="secondary" label={t('archive.exportJson')} onClick={handleExportJson} />
            )}
            <Button theme="primary" label={t('archive.exportExcel')} onClick={handleExport} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Archive;
